import React, { useEffect } from 'react'
import {DollarSign, Info, BarChart3, TrendingUp, MessageSquare} from 'lucide-react'
import { useBottles } from '../hooks/useBottles'

export default function SpecsPage() {
  const { bottles, loading, fetchBottles } = useBottles()

  useEffect(() => {
    fetchBottles({ sort: { createdAt: -1 } })
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-[#D97706] border-t-transparent mb-4"></div>
          <p className="text-[#3E2723] text-lg">Loading bottle database...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5] py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-16 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-[#3E2723] mb-6">
            Single Barrel Bottle Database
          </h1>
          <p className="text-xl text-[#3E2723]/70 max-w-3xl mx-auto leading-relaxed">
            Comprehensive specs, pricing data, and availability information for the most sought-after single barrel expressions.
          </p>
        </div>

        {/* Bottles Grid */}
        <div className="space-y-8">
          {bottles.map((bottle) => (
            <div key={bottle._id} className="bg-white rounded-xl shadow-lg border border-[#B87333]/20 overflow-hidden hover:shadow-2xl transition-all">
              <div className="grid grid-cols-1 lg:grid-cols-3">
                {/* Image Section */}
                <div className="bg-gradient-to-br from-[#D97706] to-[#B87333] h-64 lg:h-auto relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-white/90 text-6xl font-bold mb-2">{bottle.proof.split(' ')[0]}</div>
                      <div className="text-white/60 text-sm">Proof</div>
                    </div>
                  </div>
                </div>

                {/* Info Section */}
                <div className="lg:col-span-2 p-8">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-3xl font-bold text-[#3E2723] mb-2">
                        {bottle.name}
                      </h2>
                      <p className="text-[#D97706] font-medium text-lg">{bottle.brand}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-[#F59E0B] mb-1">{bottle.price}</div>
                      <div className="text-[#3E2723]/60 text-sm">{bottle.availability}</div>
                    </div>
                  </div>

                  <p className="text-[#3E2723]/80 leading-relaxed mb-6">
                    {bottle.description}
                  </p>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-[#FAF8F5] p-4 rounded-lg border border-[#B87333]/10">
                      <div className="flex items-center space-x-2 mb-2">
                        <BarChart3 className="w-4 h-4 text-[#D97706]" />
                        <span className="text-xs text-[#3E2723]/60 font-medium">PROOF</span>
                      </div>
                      <p className="text-[#3E2723] font-bold">{bottle.proof}</p>
                    </div>

                    <div className="bg-[#FAF8F5] p-4 rounded-lg border border-[#B87333]/10">
                      <div className="flex items-center space-x-2 mb-2">
                        <Info className="w-4 h-4 text-[#D97706]" />
                        <span className="text-xs text-[#3E2723]/60 font-medium">AGE</span>
                      </div>
                      <p className="text-[#3E2723] font-bold">{bottle.age}</p>
                    </div>

                    <div className="bg-[#FAF8F5] p-4 rounded-lg border border-[#B87333]/10">
                      <div className="flex items-center space-x-2 mb-2">
                        <DollarSign className="w-4 h-4 text-[#D97706]" />
                        <span className="text-xs text-[#3E2723]/60 font-medium">MSRP</span>
                      </div>
                      <p className="text-[#3E2723] font-bold">{bottle.price}</p>
                    </div>

                    <div className="bg-[#FAF8F5] p-4 rounded-lg border border-[#B87333]/10">
                      <div className="flex items-center space-x-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-[#D97706]" />
                        <span className="text-xs text-[#3E2723]/60 font-medium">STATUS</span>
                      </div>
                      <p className="text-[#3E2723] font-bold text-sm">{bottle.availability}</p>
                    </div>
                  </div>

                  <div className="bg-[#F59E0B]/5 border border-[#F59E0B]/20 rounded-lg p-4">
                    <p className="text-xs text-[#3E2723]/60 mb-1">SEO Keywords:</p>
                    <p className="text-sm text-[#3E2723]/80 italic">{bottle.keywords}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Coming Soon CTA */}
        <div className="mt-16 bg-gradient-to-r from-[#3E2723] to-[#4E342E] rounded-2xl p-12 text-center border border-[#B87333]/30">
          <MessageSquare className="w-16 h-16 text-[#F59E0B] mx-auto mb-6" />
          <h3 className="text-3xl font-bold text-[#FAF8F5] mb-4">
            More Bottles Coming Soon
          </h3>
          <p className="text-[#FAF8F5]/80 max-w-2xl mx-auto mb-6">
            We're continuously adding detailed specs, pricing data, and community reviews for more single barrel expressions.
          </p>
          <p className="text-[#F59E0B] text-sm">
            Database: {bottles.length} bottles loaded dynamically from MongoDB
          </p>
        </div>
      </div>
    </div>
  )
}
